/**
 * This new version of <split-text> supports any html tag, thus allowing usage
 * of it on WYSIWYG blocks, and can even include img/svg.
 * This new version also allows different split modes using the by="" attribute: 
 *  by="letters", by="words" or by="lines"
 * For back compatibility, this component is called <split-text-new>
 */
function getPosition( node, scope ) {
	const root = scope || document;
	const width = node.clientWidth;
	const height = node.clientHeight;
	let offsetTop = node.offsetTop;
	let offsetLeft = node.offsetLeft;
	while ( node && node.offsetParent && node.offsetParent != document && node !== root && root !== node.offsetParent ) {
		offsetTop += node.offsetParent.offsetTop;
		offsetLeft += node.offsetParent.offsetLeft;
		node = node.offsetParent;
	}
	return {
		top: offsetTop,
		left: offsetLeft,
		width,
		height,
	};
}
 
const INLINE_TAGS = new Set([
  'A', 'ABBR', 'B', 'BDI', 'BDO', 'BR', 'CITE', 'CODE', 'DATA', 'DFN',
  'EM', 'I', 'KBD', 'MARK', 'Q', 'S', 'SAMP', 'SMALL', 'SPAN',
  'STRONG', 'SUB', 'SUP', 'TIME', 'U', 'VAR'
]);

const BLOCK_SELECTOR = `p, h1, h2, h3, h4, h5, h6, li, blockquote, figcaption, td, th`;

function isGeneratedSpace(el) {
    return (
        el &&
        el.nodeType === 1 &&
        el.hasAttribute('data-space')
    );
}

function trimLineSpaces(words) {
    const out = [...words];
    while (out.length && isGeneratedSpace(out[0])) {
        out.shift();
    }
    while (out.length && isGeneratedSpace(out[out.length - 1])) {
        out.pop();
    }
    return out;
}

function pushLine(lines, words) {
    const cleaned = trimLineSpaces(words);
    if (cleaned.length && lineHasRealContent(cleaned)) {
        lines.push(cleaned);
    }
}

function lineHasRealContent(words) {
    return words.some(el =>
        el.hasAttribute('data-line-break') ||
        (
            !el.hasAttribute('data-space') &&
            el.textContent.trim() !== ''
        )
    );
}

function isInlineFormattingElement(el) {
    if (!el || !el.tagName) return false;
    if (el.hasAttribute('data-word')) return false;
    const display = getComputedStyle(el).display;
    return (
        INLINE_TAGS.has(el.tagName) ||
        display === 'inline' ||
        display === 'inline-block' ||
        display === 'inline-flex' ||
        display === 'inline-grid'
    );
}

function getInlineChain(node, stopAt) {
    const chain = [];
    let el = node.parentElement;
    while (el && el !== stopAt) {
        if (isInlineFormattingElement(el)) {
            chain.unshift(el);
        }
        el = el.parentElement;
    }
    return chain;
}

function getSplitBlocks(root) {
    const blocks = [...root.querySelectorAll(BLOCK_SELECTOR)];
    const deepestBlocks = blocks.filter(block => {
        return !blocks.some(other => other !== block && block.contains(other));
    });
    if (!deepestBlocks.length) {
        return [root];
    }
    return deepestBlocks;
}

function buildLine(words, lineIndex, root, stopAt) {
    const wrapper = document.createElement('div');
    wrapper.style.display = 'block';
    wrapper.className = 'line-wrapper';
    wrapper.dataset.lineWrapper = '';
    wrapper.dataset.lineIndex = lineIndex;
    wrapper.style.setProperty('--line-index', lineIndex);
    wrapper.setAttribute('aria-hidden', true);

    const line = document.createElement('div');
    line.className = 'line';
    line.style.display = 'block';
    line.dataset.line = '';
    line.dataset.lineIndex = lineIndex;
    line.setAttribute('aria-hidden', true);

    wrapper.appendChild(line);

    let currentChain = [];
    let container = line;

    words.forEach(word => {
        const chain = getInlineChain(word, stopAt);

        let depth = 0;
        while (
            depth < currentChain.length &&
            depth < chain.length &&
            currentChain[depth] === chain[depth]
        ) {
            depth++;
        }

        while (currentChain.length > depth) {
            container = container.parentNode;
            currentChain.pop();
        }

        for (let i = depth; i < chain.length; i++) {
            const clone = chain[i].cloneNode(false);
            container.appendChild(clone);
            container = clone;
            currentChain.push(chain[i]);
        }

        const shouldUnwrapGeneratedWord =
            shouldCleanWordSpans(root) &&
            word.hasAttribute('data-word') &&
            !word.hasAttribute('data-media-word') &&
            !word.hasAttribute('data-line-break');

        if (shouldUnwrapGeneratedWord) {
            unwrapWord(word, container);
        } else {
            container.appendChild(word);
        }
    });

    return wrapper;
}


function unwrapWord(word, container) {
    if (
        word.childNodes.length === 1 &&
        word.firstChild.nodeType === 1 &&
        word.firstChild.tagName === 'SPAN'
    ) {
        while (word.firstChild.firstChild) {
            container.appendChild(word.firstChild.firstChild);
        }
        return;
    }
    while (word.firstChild) {
        container.appendChild(word.firstChild);
    }
}

function shouldCleanWordSpans(root) {
    return root?.mode === 'lines';
}

export default class SplitText extends HTMLElement {

    constructor() {
        super();
        this.onResize = this.onResize.bind(this);

        this.autoAnimate = true;

        if (this.hasAttribute('auto-animate')) {
            this.autoAnimate = this.getAttribute('auto-animate') === 'false' ? false : true;
        }
        if ( this.autoAnimate ) {
            this.intersectionObserver = new IntersectionObserver((entries)=>{
                entries.forEach((el)=>{
                    if (el.isIntersecting) {
                        el.target.classList.add('in-view')
                    }
                });
            });
        }
    }

    connectedCallback() {

        // In the block editor React owns the preview DOM; rewriting our children
        // makes React's removeChild throw and kills the block preview. Show the
        // plain text there instead of splitting.
        if (this.closest('.acf-block-preview, .block-editor-block-list__layout, .editor-styles-wrapper')) {
            this.classList.add('ready');
            this.isEditorPreview = true;
            return;
        }

        this.mode = this.hasAttribute('by') ? this.getAttribute('by') : 'lines';
        this.debug = this.hasAttribute('debug');

        this.originalText = this.innerHTML;
        this.innerHTML = `
            <div class="sr-only" data-split-original>${this.originalText}</div>
            <div aria-hidden="true" data-split-visual>${this.originalText}</div>
        `;
        this.$visual = this.querySelector('[data-split-visual]');

        this.numLines = 0;
        
        let label = this.originalText;
        if (typeof label !== 'string') label = String(label ?? '');
        label = label.replace(/<[^>]*>/g, '');
        label = label.trim();
        label = label.replace(/[\r\n\t]+/g, ' ');
        // this.setAttribute('aria-label', label);
        // this.setAttribute('aria-hidden', true);

        this.intersectionObserver?.observe(this);

        this.resize();
        this.setupResizeObserver();
        this.afterLayoutReady(() => {
            this.resize();
            this.syncObservedWidths();
            requestAnimationFrame(() => {
                if (!this.isConnected) return;
                this.markReady();
            });
        });
        
    }

    setupResizeObserver() {
        const roundWidth = el => Math.round(el.getBoundingClientRect().width);
        this.lastWidths = new WeakMap();
        this.lastWidths.set(this, roundWidth(this));
        this.lastWidths.set(document.body, roundWidth(document.body));
        this.resizeObserver = new ResizeObserver(entries => {
            if (this.isResizing) return;
            let changed = false;
            for (const entry of entries) {
                const width = Math.round(entry.contentRect.width);
                const prevWidth = this.lastWidths.get(entry.target);

                if (prevWidth !== width) {
                    this.lastWidths.set(entry.target, width);
                    changed = true;
                }
            }
            if (changed) {
                this.onResize();
            }
        });
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(document.body);
    }

    rerender({ animate = false } = {}) {
        const wasReady = this.classList.contains('ready');
        if (animate) {
            this.classList.remove('ready');
        }
        this.resize();
        if (animate && wasReady && this.classList.contains('in-view')) {
            // Force browser to commit the hidden state before restoring .ready.
            this.offsetHeight;
            requestAnimationFrame(() => {
                if (!this.isConnected) return;
                this.markReady();
            });
        }
    }

    syncObservedWidths() {
        if (!this.lastWidths) return;
        const roundWidth = el => Math.round(el.getBoundingClientRect().width);
        this.lastWidths.set(this, roundWidth(this));
        this.lastWidths.set(document.body, roundWidth(document.body));
    }

    afterLayoutReady(callback) {
        const waitForFonts = document.fonts?.ready || Promise.resolve();
        const waitForImages = Promise.all(
            [...this.querySelectorAll('img')]
                .filter(img => !img.complete)
                .map(img => {
                    if (img.decode) {
                        return img.decode().catch(() => {});
                    }
                    return new Promise(resolve => {
                        img.addEventListener('load', resolve, { once: true });
                        img.addEventListener('error', resolve, { once: true });
                    });
                })
        );
        const fallback = new Promise(resolve => {
            setTimeout(resolve, 700);
        });
        Promise.race([
            Promise.all([waitForFonts, waitForImages]),
            fallback
        ]).then(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (!this.isConnected) return;
                    callback();
                });
            });
        });
    }

    disconnectedCallback() {
        this.intersectionObserver?.unobserve(this);
        if (this.resizeObserver) {
            this.resizeObserver.unobserve(this);
            this.resizeObserver.unobserve(document.body);
            this.resizeObserver.disconnect();
        }
    }

    nestedEach(node, cb){
        cb(node);
        node?.childNodes?.forEach(n => {
            this.nestedEach( n, cb );
        });
    }


    onResize() {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            if (this.isResizing) return;
            this.isResizing = true;
            const wasReady = this.classList.contains('ready');
            // Prevent transition flicker while rebuilding.
            this.classList.add('is-resizing');
            this.classList.remove('ready');
            this.resize();
            requestAnimationFrame(() => {
                this.syncObservedWidths();
                if (wasReady) {
                    this.markReady();
                }
                requestAnimationFrame(() => {
                    this.classList.remove('is-resizing');
                    this.isResizing = false;
                });
            });
        }, 150);
    }

    markReady() {
        const delay = this.getAttribute('data-delay') || this.getAttribute('delay') || 0;
        this.style.setProperty('--delay', Number(delay));
        this.classList.add('ready');
    }
   
    resize() {
        if (!this.$visual) return;

        this.$visual.innerHTML = this.originalText;
        this.splitContent(this.$visual);
        if (this.mode === 'words') {
            this.indexWords();
            return;
        }
        if (this.mode === 'letters') {
            return;
        }
        this.wrapLines();
    }

    indexWords() {
        let wordIndex = 0;
        const words = [
        ...(this.$visual || this).querySelectorAll('[data-word]:not([data-space]):not([data-line-break])')
        ];
        words.forEach(word => {
            word.setAttribute('data-word-index', wordIndex);
            word.style.setProperty('--word-index', wordIndex);
            wordIndex++;
        });
    }


    wrapLines() {
        const blocks = getSplitBlocks(this.$visual || this);
        this.numLines = 0;
        let globalLineIndex = 0;
        blocks.forEach(block => {
            let currY = -1;
            let lineIndex = 0;
            let currLine = [];
            let hasAnyLine = false;

            const words = [...block.querySelectorAll('[data-word], [data-line-break]')];
            if (!words.length) return;
            const lines = [];

            let previousWasLineBreak = false;
            words.forEach(el => {
                const isLineBreak = el.hasAttribute('data-line-break');
                if (isLineBreak) {
                    // Single BR: only ends the current visual line.
                    pushLine(lines, currLine);
                    currLine = [];
                    // Double BR: second BR becomes its own wrapped empty line.
                    if (previousWasLineBreak) {
                        lines.push([el]);
                    }
                    hasAnyLine = false;
                    currY = -1;
                    lineIndex++;
                    previousWasLineBreak = true;
                    return;
                }
                previousWasLineBreak = false;
                const p = getPosition(el, block);
                if (!hasAnyLine) {
                    currY = p.top;
                    lineIndex = 0;
                }
                if (p.top > currY + 3) {
                    pushLine(lines, currLine);
                    currY = p.top;
                    lineIndex++;
                    currLine = [];
                }
                currLine.push(el);
                hasAnyLine = true;
            });

            if (currLine.length && lineHasRealContent(currLine)) {
                pushLine(lines, currLine);
            }

            const fragment = document.createDocumentFragment();

            lines.forEach((lineWords, localLineIndex) => {
                const realLineIndex = globalLineIndex + localLineIndex;
                const line = buildLine(lineWords, realLineIndex, this, block);
                fragment.appendChild(line);
            });

            block.innerHTML = '';
            block.appendChild(fragment);

            globalLineIndex += lines.length;
            this.numLines += lines.length;
        });

        this.querySelectorAll('li').forEach(li => {
            const firstLine = li.querySelector('[data-line-wrapper]');
            if (!firstLine) return;

            const lineIndex =
                firstLine.getAttribute('data-line-index') ||
                firstLine.style.getPropertyValue('--line-index');

            if (lineIndex == null || lineIndex === '') return;

            li.setAttribute('data-line-index', lineIndex);
            li.style.setProperty('--line-index', lineIndex);
        });
    }

    _getAncestorChain(node) {
        const chain = [];
        let el = node.parentElement;
        while (el && el !== this) {
            chain.unshift(el);
            el = el.parentElement;
        }
        return chain;
    }

    getWords(text) {
        if (!text) return [];
        let parts;
        if (window.Intl?.Segmenter) {
            parts = [...new Intl.Segmenter(undefined, {
                granularity: 'word'
            }).segment(text)].map(x => x.segment);
        } else {
            parts = text.split(/(\s+)/);
        }
        return this.mergeInlinePunctuation(parts);
    }

    mergeInlinePunctuation(parts) {
        const out = [];
        parts.forEach(text => {
            if (!text) return;
            const isPunctuation = /^[\p{P}]+$/u.test(text);
            const isOpening = /^[([{«“‘¿¡「『（［｛《〈【]+$/u.test(text);
            if (
                isPunctuation &&
                !isOpening &&
                out.length &&
                !/^\s+$/.test(out[out.length - 1])
            ) {
                out[out.length - 1] += text;
                return;
            }
            out.push(text);
        });
        return out;
    }

    splitContent(root = this) {

        const textNodes = [];

        this.nestedEach(root, (el) => {
            if (el.nodeType === 3 && el.textContent.trim() !== '') {
                textNodes.push(el);
            }
            if (el.nodeType !== 3 && el.tagName === 'BR') {
                el.classList.add('desktop-break');
                el.setAttribute('data-line-break', '');
            }
            if (
                el.nodeType !== 3 &&
                ['IMG', 'SVG', 'VIDEO', 'CANVAS', 'IFRAME'].includes(el.tagName)
            ) {
                el.setAttribute('data-word', '');
                el.setAttribute('data-media-word', '');
                el.style.display = 'inline-block';
                el.style.position = 'relative';
            }
        });

        textNodes.forEach((el) => {

            const parent = el.parentNode;
            if (parent) {

                const fragment = document.createDocumentFragment(); 
                
                let cleanText = el.textContent.replace(/[\r\n\t]+/g, ' ');
                if (this.mode === 'words' || this.mode === 'letters') {
                    cleanText = cleanText.trim();
                }

                const parts = this.getWords(cleanText);
                parts.forEach((part, partIndex) => {
                    const text = part.segment ?? part;

                    if (
                        (this.mode === 'words' || this.mode === 'letters') &&
                        /^\s+$/.test(text) &&
                        (partIndex === 0 || partIndex === parts.length - 1)
                    ) {
                        return;
                    }

                    if (/^\s+$/.test(text)) {
                        if (this.mode === 'words' || this.mode === 'letters') {
                            fragment.appendChild(document.createTextNode(' '));
                            return;
                        }
                        const $outerSpan = document.createElement('span');
                        $outerSpan.setAttribute('data-word', '');
                        $outerSpan.setAttribute('data-space', '');
                        $outerSpan.style.display = 'inline-block';
                        $outerSpan.style.position = 'relative';
                        $outerSpan.style.whiteSpace = 'pre';
                        const $innerSpan = document.createElement('span');
                        $innerSpan.textContent = ' ';
                        $outerSpan.appendChild($innerSpan);
                        fragment.appendChild($outerSpan);
                        return;
                    }

                    const $outerSpan = document.createElement('span');
                    $outerSpan.setAttribute('data-word', '');
                    $outerSpan.setAttribute('data-word-count', '');
                    $outerSpan.style.display  = 'inline-block';
                    $outerSpan.style.position = 'relative';
                    $outerSpan.classList.add('text-line');

                    const $innerSpan = document.createElement('span');

                    if (this.mode === 'letters') {
                        [...text].forEach((letter, letterIndex) => {
                            const $letter = document.createElement('span');
                            $letter.dataset.letter = '';
                            $letter.dataset.letterIndex = letterIndex;
                            $letter.style.setProperty('--letter-index', letterIndex);
                            $letter.style.setProperty('--delay', `${Math.random() * (0.05 * text.length)}s`);
                            $letter.textContent = letter;
                            $innerSpan.appendChild($letter);
                        });
                    } else {
                        $innerSpan.textContent = text;
                    }

                    $outerSpan.appendChild($innerSpan);
                    fragment.appendChild($outerSpan);
                });
                parent.replaceChild(fragment, el);
            }
        });

    }

}

customElements.define('split-text', SplitText);